import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthStore, useBranchStore, useThemeStore } from '../../store';
import { branchAPI } from '../../services/api';
import wsService from '../../services/websocket';
import './Header.css';

const ROLE_LABELS = {
    superadmin:   'Super Admin',
    tenant_admin: 'Tenant Admin',
    branch_admin: 'Branch Admin',
    support:      'Support',
    viewer:       'Viewer',
};

const Header = ({ toggleSidebar }) => {
    const navigate = useNavigate();

    const logout     = useAuthStore((s) => s.logout);
    const user       = useAuthStore((s) => s.admin);
    const { theme, toggleTheme } = useThemeStore();
    const { selectedBranch, setSelectedBranch, setBranches, branches } = useBranchStore();

    const [showBranchDropdown, setShowBranchDropdown]   = useState(false);
    const [showUserDropdown, setShowUserDropdown]         = useState(false);
    const [wsStatus, setWsStatus]                         = useState('connecting'); // 'connected' | 'disconnected' | 'connecting'
    const [searchQuery, setSearchQuery]                   = useState('');
    const [branchSearch, setBranchSearch]                 = useState('');

    const branchRef = useRef(null);
    const userRef   = useRef(null);

    // ── Fetch branches ──
    // Superadmin: no tenantId filter → all branches across all tenants (limit 1000)
    // Other roles: backend enforces their tenantId automatically
    const fetchBranches = useCallback(async () => {
        try {
            const isSuperAdmin = user?.role === 'superadmin';
            const response = await branchAPI.getAll(undefined, { limit: isSuperAdmin ? 1000 : 100 });
            const list = response.data.data?.branches || response.data.data || [];
            setBranches(list);
            // Superadmin defaults to "All Branches" — never auto-select
            if (!isSuperAdmin && !selectedBranch && list.length > 0) {
                const preferred = user?.branchId
                    ? list.find(b => String(b._id) === String(user.branchId)) || list[0]
                    : list[0];
                setSelectedBranch(preferred);
            }
            // For non-superadmin: reset if selected branch no longer in list (tenant changed)
            if (!isSuperAdmin && selectedBranch && user?.tenantId) {
                const stillValid = list.some(b => String(b._id) === String(selectedBranch._id));
                if (!stillValid) setSelectedBranch(list[0] || null);
            }
        } catch (_) {}
    }, [user?.tenantId, user?.branchId, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchBranches(); }, [user?.tenantId, user?.role]); // re-fetch on login/role change

    // ── WebSocket status tracking + auth ──
    useEffect(() => {
        const onConnect    = () => {
            setWsStatus('connected');
            // Send AUTH so server scopes updates to this tenant
            const token = useAuthStore.getState().token;
            if (token) wsService.send({ type: 'AUTH', token });
        };
        const onDisconnect = () => setWsStatus('disconnected');

        wsService.on('connected', onConnect);
        wsService.on('disconnected', onDisconnect);

        // Reflect current state immediately
        setWsStatus(wsService.isConnected ? 'connected' : 'disconnected');

        return () => {
            wsService.off('connected', onConnect);
            wsService.off('disconnected', onDisconnect);
        };
    }, []);

    // ── Outside-click to close dropdowns ──
    useEffect(() => {
        const handleClick = (e) => {
            if (branchRef.current && !branchRef.current.contains(e.target)) {
                setShowBranchDropdown(false);
                setBranchSearch('');
            }
            if (userRef.current && !userRef.current.contains(e.target)) {
                setShowUserDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const handleBranchSelect = (branch) => {
        setSelectedBranch(branch);
        setShowBranchDropdown(false);
        setBranchSearch('');
        toast.info(`Switched to: ${branch.name}`);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    };

    const avatarSrc = user?.avatar
        || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'A')}&background=4e73df&color=fff&bold=true`;

    const isSuperAdmin = user?.role === 'superadmin';

    const filteredBranches = branches.filter(b =>
        b.name.toLowerCase().includes(branchSearch.toLowerCase()) ||
        (isSuperAdmin && b.tenantId?.name?.toLowerCase().includes(branchSearch.toLowerCase()))
    );

    // Group filtered branches by tenant for superadmin dropdown
    const groupedForDropdown = isSuperAdmin
        ? filteredBranches.reduce((acc, b) => {
            const tid   = b.tenantId?._id || 'other';
            const tname = b.tenantId?.name || 'Other';
            if (!acc[tid]) acc[tid] = { name: tname, branches: [] };
            acc[tid].branches.push(b);
            return acc;
        }, {})
        : null;

    const wsLabel = { connected: 'Live', disconnected: 'Offline', connecting: 'Connecting' }[wsStatus];

    return (
        <header className="header-modern">
            <div className="header-content">
                {/* ── Left ── */}
                <div className="header-left">
                    <button className="menu-btn" onClick={toggleSidebar} aria-label="Toggle menu">
                        <i className="fas fa-bars"></i>
                    </button>

                    {/* Branch Selector */}
                    <div className="branch-selector" ref={branchRef}>
                        <button
                            className="branch-button"
                            onClick={() => { setShowBranchDropdown(v => !v); setBranchSearch(''); if (branches.length === 0) fetchBranches(); }}
                            aria-haspopup="listbox"
                        >
                            <span className="branch-label">Branch</span>
                            <span className="branch-name">
                                {selectedBranch?.name || (isSuperAdmin ? 'All Branches' : 'Select Branch')}
                            </span>
                            <i className={`fas fa-chevron-${showBranchDropdown ? 'up' : 'down'}`}></i>
                        </button>

                        {showBranchDropdown && (
                            <div className="branch-dropdown" role="listbox">
                                {branches.length > 4 && (
                                    <div className="branch-search-wrap">
                                        <i className="fas fa-search"></i>
                                        <input
                                            autoFocus
                                            type="text"
                                            placeholder={isSuperAdmin ? 'Search branch or company…' : 'Search branch…'}
                                            value={branchSearch}
                                            onChange={e => setBranchSearch(e.target.value)}
                                            className="branch-search-input"
                                        />
                                    </div>
                                )}

                                {/* Superadmin: All Branches option */}
                                {isSuperAdmin && !branchSearch && (
                                    <div
                                        role="option"
                                        className={`branch-option ${!selectedBranch ? 'active' : ''}`}
                                        onClick={() => { setSelectedBranch(null); setShowBranchDropdown(false); setBranchSearch(''); }}
                                    >
                                        <i className="fas fa-globe branch-option-icon" style={{ color: 'var(--primary-color)' }}></i>
                                        <span style={{ fontWeight: 600 }}>All Branches</span>
                                        <span style={{ marginLeft: 'auto', fontSize: '0.75em', opacity: 0.55 }}>{branches.length}</span>
                                        {!selectedBranch && <i className="fas fa-check branch-check"></i>}
                                    </div>
                                )}

                                {filteredBranches.length === 0 ? (
                                    <div className="branch-option disabled">
                                        {branchSearch ? 'No results' : 'No branches available'}
                                    </div>
                                ) : groupedForDropdown ? (
                                    /* Superadmin — grouped by company */
                                    Object.entries(groupedForDropdown).map(([tid, group]) => (
                                        <div key={tid}>
                                            <div className="branch-group-label">
                                                <i className="fas fa-building" style={{ marginRight: 5, fontSize: '0.75em' }}></i>
                                                {group.name}
                                                <span style={{ marginLeft: 'auto', opacity: 0.5, fontSize: '0.78em' }}>{group.branches.length}</span>
                                            </div>
                                            {group.branches.map(branch => (
                                                <div
                                                    key={branch._id}
                                                    role="option"
                                                    aria-selected={selectedBranch?._id === branch._id}
                                                    className={`branch-option branch-option-indented ${selectedBranch?._id === branch._id ? 'active' : ''}`}
                                                    onClick={() => handleBranchSelect(branch)}
                                                >
                                                    <i className="fas fa-code-branch branch-option-icon"></i>
                                                    <span>{branch.name}</span>
                                                    {selectedBranch?._id === branch._id && (
                                                        <i className="fas fa-check branch-check"></i>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ))
                                ) : (
                                    /* Non-superadmin — flat list */
                                    filteredBranches.map(branch => (
                                        <div
                                            key={branch._id}
                                            role="option"
                                            aria-selected={selectedBranch?._id === branch._id}
                                            className={`branch-option ${selectedBranch?._id === branch._id ? 'active' : ''}`}
                                            onClick={() => handleBranchSelect(branch)}
                                        >
                                            <i className="fas fa-map-marker-alt branch-option-icon"></i>
                                            <span>{branch.name}</span>
                                            {selectedBranch?._id === branch._id && (
                                                <i className="fas fa-check branch-check"></i>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    {/* Search */}
                    <form className="search-bar" onSubmit={handleSearch}>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        <button type="submit" className="search-button" aria-label="Search">
                            <i className="fas fa-search"></i>
                        </button>
                    </form>
                </div>

                {/* ── Right ── */}
                <div className="header-actions">
                    {/* WS Status */}
                    <div className={`ws-status-pill ws-${wsStatus}`} title={`Realtime: ${wsLabel}`}>
                        <span className="ws-dot"></span>
                        <span className="ws-label">{wsLabel}</span>
                    </div>

                    {/* Theme toggle */}
                    <button
                        className="header-icon-btn"
                        onClick={toggleTheme}
                        aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                        title={`${theme === 'light' ? 'Dark' : 'Light'} mode`}
                    >
                        <i className={`fas fa-${theme === 'light' ? 'moon' : 'sun'}`}></i>
                    </button>

                    {/* Notifications */}
                    <button className="header-icon-btn notification-btn" aria-label="Notifications">
                        <i className="fas fa-bell"></i>
                        <span className="notification-badge">3</span>
                    </button>

                    {/* User Profile */}
                    <div className="user-profile-section" ref={userRef}>
                        <button
                            className={`user-profile-btn ${showUserDropdown ? 'open' : ''}`}
                            onClick={() => setShowUserDropdown(v => !v)}
                            aria-haspopup="true"
                            aria-expanded={showUserDropdown}
                        >
                            <div className="user-avatar-header">
                                <img src={avatarSrc} alt={user?.name || 'User'} />
                            </div>
                            <div className="user-info">
                                <span className="user-name">{user?.name || 'Admin'}</span>
                                <span className="user-role">{ROLE_LABELS[user?.role] || user?.role || 'Administrator'}</span>
                            </div>
                            <i className={`fas fa-chevron-${showUserDropdown ? 'up' : 'down'}`}></i>
                        </button>

                        {showUserDropdown && (
                            <div className="user-dropdown" role="menu">
                                {/* User info card */}
                                <div className="dropdown-user-card">
                                    <div className="dropdown-avatar">
                                        <img src={avatarSrc} alt={user?.name} />
                                    </div>
                                    <div className="dropdown-user-info">
                                        <div className="dropdown-user-name">{user?.name || 'Admin'}</div>
                                        <div className="dropdown-user-email">{user?.email || ''}</div>
                                        {(user?.tenantName || user?.tenantType) && (
                                            <div className="dropdown-company">
                                                <i className="fas fa-building"></i>
                                                <span>{user?.tenantName || user?.tenantType}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="dropdown-role-badge">
                                    <i className="fas fa-shield-alt"></i>
                                    {ROLE_LABELS[user?.role] || user?.role}
                                </div>

                                <div className="dropdown-divider"></div>

                                <button
                                    role="menuitem"
                                    className="dropdown-item"
                                    onClick={() => { setShowUserDropdown(false); navigate('/profile'); }}
                                >
                                    <i className="fas fa-user-circle"></i>
                                    My Profile
                                </button>
                                <button
                                    role="menuitem"
                                    className="dropdown-item"
                                    onClick={() => { setShowUserDropdown(false); navigate('/settings'); }}
                                >
                                    <i className="fas fa-cog"></i>
                                    Settings
                                </button>
                                {(user?.role === 'superadmin' || user?.role === 'tenant_admin') && (
                                    <button
                                        role="menuitem"
                                        className="dropdown-item"
                                        onClick={() => { setShowUserDropdown(false); navigate('/payment-config'); }}
                                    >
                                        <i className="fas fa-credit-card"></i>
                                        Payment Gateways
                                    </button>
                                )}

                                <div className="dropdown-divider"></div>

                                <button
                                    role="menuitem"
                                    className="dropdown-item logout"
                                    onClick={handleLogout}
                                >
                                    <i className="fas fa-sign-out-alt"></i>
                                    Sign Out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;
